import { useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { MagnifyingGlassIcon, CheckIcon } from "@heroicons/react/24/outline";

/**
 * PMPopoverMenu
 * Portal-based fixed-position dropdown anchored to a DOMRect.
 * Dismisses on Escape, outside-click, or context-menu on the overlay.
 * With `modal={false}` no overlay is rendered: outside pointer-downs close the
 * menu but still reach the page, so the user can scroll or open a sibling
 * menu with a single click. Moving keyboard focus outside the menu (Tab /
 * Shift+Tab) also closes a non-modal menu.
 *
 * Props:
 *   anchorRect  DOMRect   Position source (from getBoundingClientRect())
 *   onClose     fn        Called when the menu should close
 *   align       'left'|'right'  Horizontal alignment relative to anchor
 *   width       number    Pixel width (default 244)
 *   minWidth    number    Optional min-width
 *   children    ReactNode Content rendered inside the popover box
 *   modal       bool      Render the click-catching overlay (default true)
 *   ignoreOutsideRef ref  Element (e.g. the trigger) whose pointer-downs and
 *                         focus do not count as outside when modal is false
 *   returnFocusRef ref    Element that receives focus after Escape closes the menu
 *   role / ariaLabel      Optional ARIA role + label for the popover box
 *                         (e.g. "dialog" when the trigger has aria-haspopup="dialog")
 */
export function PMPopoverMenu({
  anchorRect,
  onClose,
  children,
  align = "left",
  width = 244,
  minWidth,
  className = "",
  /** Overlay z-index (default for page-level popovers). Use ~2090 inside PMDialog (2000). */
  zIndexOverlay = 390,
  /** Menu z-index — must be above overlay and any parent dialog. */
  zIndexMenu = 400,
  modal = true,
  ignoreOutsideRef,
  returnFocusRef,
  role,
  ariaLabel,
}) {
  const ref = useRef(null);
  const [pos, setPos] = useState(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Position popover below (or above) the anchor rect
  useLayoutEffect(() => {
    if (!anchorRect || !ref.current) return;
    const el = ref.current;
    const elW = el.offsetWidth;
    const elH = el.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let left = align === "right" ? anchorRect.right - elW : anchorRect.left;
    let top = anchorRect.bottom + 6;
    if (left + elW > vw - 8) left = vw - elW - 8;
    if (left < 8) left = 8;
    if (top + elH > vh - 8) top = Math.max(8, anchorRect.top - elH - 6);
    setPos({ left, top });
  }, [anchorRect, align]);

  // Dismiss on Escape
  useEffect(() => {
    const k = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
        returnFocusRef?.current?.focus?.();
      }
    };
    window.addEventListener("keydown", k, true);
    return () => window.removeEventListener("keydown", k, true);
  }, [onClose, returnFocusRef]);

  // Non-modal: close on pointer-down outside the menu without swallowing it.
  useEffect(() => {
    if (modal) return undefined;
    const handle = (e) => {
      if (ref.current?.contains(e.target)) return;
      if (ignoreOutsideRef?.current?.contains(e.target)) return;
      onCloseRef.current();
    };
    document.addEventListener("pointerdown", handle, true);
    // Focus leaving via Tab lands on an element outside menu + trigger.
    document.addEventListener("focusin", handle, true);
    return () => {
      document.removeEventListener("pointerdown", handle, true);
      document.removeEventListener("focusin", handle, true);
    };
  }, [modal, ignoreOutsideRef]);

  if (typeof document === "undefined") return null;

  return ReactDOM.createPortal(
    <>
      {/* Invisible overlay captures outside clicks */}
      {modal && (
        <div
          className="fixed inset-0"
          style={{ zIndex: zIndexOverlay }}
          onClick={(e) => { e.stopPropagation(); onClose(); }}
          onContextMenu={(e) => { e.preventDefault(); onClose(); }}
        />
      )}
      <div
        ref={ref}
        role={role}
        aria-label={ariaLabel}
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "fixed",
          left: pos ? pos.left : -9999,
          top: pos ? pos.top : -9999,
          width: width || undefined,
          minWidth: minWidth || undefined,
          visibility: pos ? "visible" : "hidden",
          zIndex: zIndexMenu,
          animation: "pm-popin 0.13s ease",
        }}
        className={`rounded-[14px] border border-slate-200 bg-white py-1.5 shadow-[0_16px_44px_rgba(0,0,0,0.20),0_4px_12px_rgba(0,0,0,0.10)] ${className}`}
      >
        <style>{`@keyframes pm-popin{from{opacity:0;transform:translateY(-4px) scale(.985)}}`}</style>
        {children}
      </div>
    </>,
    document.body
  );
}

/**
 * PMSelectMenu
 * Multi or single-select dropdown with optional search.
 *
 * Props:
 *   anchorRect    DOMRect
 *   onClose       fn
 *   options       [{ id, label, ...extras }]
 *   value         string[] (multi) | string|null (single)
 *   onPick        (id) => void    — called on each toggle; popover stays open for multi
 *   multi         bool            — multi-select with checkboxes
 *   searchable    bool            — show search input (default true)
 *   renderOption  (option) => ReactNode  — custom option renderer
 *   align         'left'|'right'
 *   label         string          — section label above options
 *   width         number
 *   modal / ignoreOutsideRef / returnFocusRef / role / ariaLabel   forwarded to PMPopoverMenu
 */
export function PMSelectMenu({
  anchorRect,
  onClose,
  options = [],
  value,
  onPick,
  multi = false,
  searchable = true,
  renderOption,
  align = "left",
  label,
  width = 244,
  zIndexOverlay,
  zIndexMenu,
  className,
  searchPlaceholder = "Søk…",
  emptyLabel = "Ingen treff",
  modal,
  ignoreOutsideRef,
  returnFocusRef,
  role,
  ariaLabel,
}) {
  const [q, setQ] = useState("");
  const inputRef = useRef(null);

  const filtered = q
    ? options.filter((o) => o.label.toLowerCase().includes(q.toLowerCase()))
    : options;

  const sel = new Set(
    multi
      ? Array.isArray(value) ? value : []
      : value != null ? [String(value)] : []
  );

  // Auto-focus search input
  useEffect(() => {
    if (searchable) {
      const t = setTimeout(() => inputRef.current?.focus(), 15);
      return () => clearTimeout(t);
    }
  }, [searchable]);

  return (
    <PMPopoverMenu
      anchorRect={anchorRect}
      onClose={onClose}
      align={align}
      width={width}
      zIndexOverlay={zIndexOverlay}
      zIndexMenu={zIndexMenu}
      className={className}
      modal={modal}
      ignoreOutsideRef={ignoreOutsideRef}
      returnFocusRef={returnFocusRef}
      role={role}
      ariaLabel={ariaLabel}
    >
      {searchable && (
        <div className="flex items-center gap-2 border-b border-slate-100 px-3 py-2 -mt-1.5 mb-1">
          <MagnifyingGlassIcon className="h-[14px] w-[14px] flex-shrink-0 text-slate-400" />
          <input
            ref={inputRef}
            placeholder={searchPlaceholder}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="flex-1 border-none bg-transparent text-[14px] text-slate-900 outline-none placeholder:text-slate-400"
          />
        </div>
      )}
      {label && (
        <div className="px-3 py-1 text-[10.5px] font-semibold uppercase tracking-wider text-slate-400">
          {label}
        </div>
      )}
      <div style={{ maxHeight: 300, overflowY: "auto" }}>
        {filtered.length === 0 ? (
          <div className="px-3 py-2 text-sm text-slate-400">{emptyLabel}</div>
        ) : (
          filtered.map((o) => {
            const id = String(o.id ?? "none");
            const active = sel.has(String(o.id));
            return (
              <button
                key={id}
                type="button"
                aria-pressed={multi ? active : undefined}
                className={`flex w-full items-center gap-2.5 rounded-[9px] px-3 py-2 text-left text-[14px] text-slate-800 hover:bg-slate-50${active ? " is-selected" : ""}`}
                onClick={() => {
                  onPick(o.id);
                  if (!multi) onClose();
                }}
              >
                {/* Multi-select checkbox */}
                {multi && (
                  <span
                    className={`flex h-[16px] w-[16px] flex-shrink-0 items-center justify-center rounded-[5px] border-[1.5px] transition-colors ${
                      active
                        ? "border-blue-500 bg-blue-500 text-white"
                        : "border-slate-300 bg-white"
                    }`}
                  >
                    {active && (
                      <CheckIcon className="h-[10px] w-[10px]" strokeWidth={3} />
                    )}
                  </span>
                )}

                {/* Option content */}
                {renderOption ? (
                  <span className="min-w-0 flex-1">{renderOption(o)}</span>
                ) : (
                  <span className="min-w-0 flex-1 truncate">{o.label}</span>
                )}

                {/* Single-select checkmark */}
                {!multi && active && (
                  <CheckIcon className="ml-auto h-[15px] w-[15px] flex-shrink-0 text-blue-500" />
                )}
              </button>
            );
          })
        )}
      </div>
    </PMPopoverMenu>
  );
}
